import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Plus, Copy, Mail, Video, Users, Calendar, Clock, 
  DollarSign, CheckCircle, XCircle, Trash2, RefreshCw,
  Link as LinkIcon, Send, Eye, Play
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';
const APP_URL = window.location.origin;

const AdminSessionManager = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  
  // Create session form
  const [newSession, setNewSession] = useState({
    title: '',
    clientEmail: '',
    requiresPayment: false,
    price: 100,
    notes: ''
  });
  
  useEffect(() => {
    fetchSessions();
  }, []);
  
  const fetchSessions = async () => {
    try {
      const res = await fetch(`${API}/api/live-studio/admin/sessions`);
      if (res.ok) {
        const data = await res.json();
        setSessions(data.sessions || []);
      }
    } catch (err) {
      console.error('Error fetching sessions:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const createSession = async () => {
    if (!newSession.title) {
      toast.error('Please enter a session title');
      return;
    }
    
    try {
      const res = await fetch(`${API}/api/live-studio/admin/sessions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: newSession.title,
          client_email: newSession.clientEmail,
          requires_payment: newSession.requiresPayment,
          price: newSession.requiresPayment ? newSession.price : 0,
          notes: newSession.notes
        })
      });
      
      if (!res.ok) throw new Error('Failed to create session');
      
      const data = await res.json();
      toast.success('Session created! Copy the link to share.');
      
      // Reset form and close modal
      setNewSession({ title: '', clientEmail: '', requiresPayment: false, price: 100, notes: '' });
      setShowCreateModal(false);
      
      // Show the new session details
      setSelectedSession(data.session);
      
      // Refresh list
      fetchSessions();
    } catch (err) {
      toast.error('Failed to create session');
    }
  };
  
  const copyLink = (code) => {
    const link = `${APP_URL}/join/${code}`;
    navigator.clipboard.writeText(link);
    toast.success('Link copied to clipboard!');
  };
  
  const sendInvite = async (session) => {
    if (!session.client_email) {
      toast.error('No client email set for this session');
      return;
    }
    
    try {
      const res = await fetch(`${API}/api/live-studio/admin/sessions/${session.code}/send-invite`, {
        method: 'POST'
      });
      
      if (res.ok) {
        toast.success(`Invite sent to ${session.client_email}`);
      } else {
        throw new Error('Failed to send');
      }
    } catch (err) {
      toast.error('Failed to send invite email');
    }
  };
  
  const deleteSession = async (code) => {
    if (!confirm('Are you sure you want to delete this session?')) return;
    
    try {
      const res = await fetch(`${API}/api/live-studio/admin/sessions/${code}`, {
        method: 'DELETE'
      });
      
      if (res.ok) {
        toast.success('Session deleted');
        fetchSessions();
        if (selectedSession?.code === code) {
          setSelectedSession(null);
        }
      }
    } catch (err) {
      toast.error('Failed to delete session');
    }
  };
  
  const getStatusBadge = (session) => {
    if (session.client_joined) {
      return <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded-full">Client Joined</span>;
    }
    if (session.client_submitted) {
      return <span className="px-2 py-1 bg-blue-500/20 text-blue-400 text-xs rounded-full">Form Submitted</span>;
    }
    if (session.invite_sent) {
      return <span className="px-2 py-1 bg-amber-500/20 text-amber-400 text-xs rounded-full">Invite Sent</span>;
    }
    return <span className="px-2 py-1 bg-gray-500/20 text-gray-400 text-xs rounded-full">Pending</span>;
  };
  
  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <Video className="w-8 h-8 text-green-500" />
              Session Manager
            </h1>
            <p className="text-gray-400 mt-1">Create and manage live studio session invites</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={fetchSessions}
              className="px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-6 py-2 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-400 hover:to-emerald-400 text-black font-bold rounded-lg flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              New Session
            </button>
          </div>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Sessions List */}
          <div className="lg:col-span-2 space-y-4">
            <h2 className="text-xl font-bold mb-4">Your Sessions</h2>
            
            {loading ? (
              <div className="bg-[#141414] rounded-xl p-8 text-center">
                <RefreshCw className="w-8 h-8 text-green-500 animate-spin mx-auto" />
              </div>
            ) : sessions.length === 0 ? (
              <div className="bg-[#141414] rounded-xl p-8 text-center">
                <Video className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-500">No sessions yet</p>
                <p className="text-gray-600 text-sm mt-1">Create a session to get started</p>
              </div>
            ) : (
              sessions.map(session => (
                <motion.div
                  key={session.code}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`bg-[#141414] rounded-xl p-4 cursor-pointer transition-all ${
                    selectedSession?.code === session.code ? 'ring-2 ring-green-500' : 'hover:bg-[#1a1a1a]'
                  }`}
                  onClick={() => setSelectedSession(session)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-bold text-lg">{session.title}</h3>
                        {getStatusBadge(session)}
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-400">
                        {session.client_email && (
                          <span className="flex items-center gap-1">
                            <Mail className="w-4 h-4" />
                            {session.client_email}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <LinkIcon className="w-4 h-4" />
                          {session.code}
                        </span>
                        {session.requires_payment && (
                          <span className="flex items-center gap-1 text-green-400">
                            <DollarSign className="w-4 h-4" />
                            ${session.price}
                          </span>
                        )}
                        {!session.requires_payment && (
                          <span className="text-green-400 text-xs">FREE</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => { e.stopPropagation(); copyLink(session.code); }}
                        className="p-2 bg-gray-800 hover:bg-gray-700 rounded-lg"
                        title="Copy Link"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                      {session.client_email && (
                        <button
                          onClick={(e) => { e.stopPropagation(); sendInvite(session); }}
                          className="p-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded-lg"
                          title="Send Invite"
                        >
                          <Send className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={(e) => { e.stopPropagation(); deleteSession(session.code); }}
                        className="p-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))
            )}
          </div>
          
          {/* Session Details Panel */}
          <div className="lg:col-span-1">
            {selectedSession ? (
              <div className="bg-[#141414] rounded-xl p-6 sticky top-6">
                <h2 className="text-xl font-bold mb-4">Session Details</h2>
                
                <div className="space-y-4">
                  <div>
                    <label className="text-sm text-gray-400">Title</label>
                    <p className="font-semibold">{selectedSession.title}</p>
                  </div>
                  
                  <div>
                    <label className="text-sm text-gray-400">Session Code</label>
                    <p className="font-mono text-green-400">{selectedSession.code}</p>
                  </div>
                  
                  <div>
                    <label className="text-sm text-gray-400">Client Join Link (Form)</label>
                    <div className="flex items-center gap-2 mt-1">
                      <input
                        type="text"
                        readOnly
                        value={`${APP_URL}/join/${selectedSession.code}`}
                        className="flex-1 px-3 py-2 bg-[#0a0a0a] border border-gray-800 rounded-lg text-sm"
                      />
                      <button
                        onClick={() => copyLink(selectedSession.code)}
                        className="p-2 bg-green-500 hover:bg-green-400 text-black rounded-lg"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  
                  <div>
                    <label className="text-sm text-gray-400">Direct Video Room (Skip Form)</label>
                    <div className="flex items-center gap-2 mt-1">
                      <input
                        type="text"
                        readOnly
                        value={`${APP_URL}/video-room/${selectedSession.code}`}
                        className="flex-1 px-3 py-2 bg-[#0a0a0a] border border-gray-800 rounded-lg text-sm"
                      />
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(`${APP_URL}/video-room/${selectedSession.code}`);
                          toast.success('Direct video room link copied!');
                        }}
                        className="p-2 bg-blue-500 hover:bg-blue-400 text-white rounded-lg"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Client goes directly to video (no form)</p>
                  </div>
                  
                  {selectedSession.client_email && (
                    <div>
                      <label className="text-sm text-gray-400">Client Email</label>
                      <p>{selectedSession.client_email}</p>
                    </div>
                  )}
                  
                  <div>
                    <label className="text-sm text-gray-400">Payment</label>
                    <p className={selectedSession.requires_payment ? 'text-green-400' : 'text-gray-400'}>
                      {selectedSession.requires_payment ? `$${selectedSession.price} required` : 'Free session'}
                    </p>
                  </div>
                  
                  {/* Client Submission Details */}
                  {selectedSession.submission && (
                    <div className="border-t border-gray-800 pt-4 mt-4">
                      <h3 className="font-bold mb-3 flex items-center gap-2">
                        <Users className="w-4 h-4 text-green-500" />
                        Client Submission
                      </h3>
                      <div className="space-y-2 text-sm">
                        <p><span className="text-gray-400">Name:</span> {selectedSession.submission.name}</p>
                        <p><span className="text-gray-400">Email:</span> {selectedSession.submission.email}</p>
                        <p><span className="text-gray-400">Expectations:</span> {selectedSession.submission.expectations}</p>
                        <p><span className="text-gray-400">Tracks:</span> {selectedSession.submission.trackCount}</p>
                        <p><span className="text-gray-400">Duration:</span> {selectedSession.submission.duration} hour(s)</p>
                        {selectedSession.submission.instruments?.length > 0 && (
                          <p><span className="text-gray-400">Instruments:</span> {selectedSession.submission.instruments.join(', ')}</p>
                        )}
                        {selectedSession.submission.additionalNotes && (
                          <p><span className="text-gray-400">Notes:</span> {selectedSession.submission.additionalNotes}</p>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Actions */}
                  <div className="border-t border-gray-800 pt-4 mt-4 space-y-2">
                    <a
                      href={`/video-room/${selectedSession.code}/host`}
                      className="w-full py-3 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-400 hover:to-emerald-400 text-black font-bold rounded-xl flex items-center justify-center gap-2"
                    >
                      <Play className="w-5 h-5" />
                      Join as Host
                    </a>
                    {selectedSession.client_email && (
                      <button
                        onClick={() => sendInvite(selectedSession)}
                        className="w-full py-3 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded-xl flex items-center justify-center gap-2"
                      >
                        <Send className="w-5 h-5" />
                        Send Invite Email
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-[#141414] rounded-xl p-6 text-center">
                <Eye className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-500">Select a session to view details</p>
              </div>
            )}
          </div>
        </div>
        
        {/* Create Session Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-[#141414] rounded-2xl p-6 max-w-md w-full"
            >
              <h2 className="text-2xl font-bold mb-6">Create New Session</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Session Title *</label>
                  <input
                    type="text"
                    value={newSession.title}
                    onChange={(e) => setNewSession({ ...newSession, title: e.target.value })}
                    className="w-full px-4 py-3 bg-[#0a0a0a] border border-gray-800 rounded-xl focus:border-green-500 focus:outline-none"
                    placeholder="e.g., Drum Recording with John"
                  />
                </div>
                
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Client Email (optional)</label>
                  <input
                    type="email"
                    value={newSession.clientEmail}
                    onChange={(e) => setNewSession({ ...newSession, clientEmail: e.target.value })}
                    className="w-full px-4 py-3 bg-[#0a0a0a] border border-gray-800 rounded-xl focus:border-green-500 focus:outline-none"
                    placeholder="client@example.com"
                  />
                  <p className="text-xs text-gray-500 mt-1">If provided, you can send an invite email</p>
                </div>
                
                <div>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newSession.requiresPayment}
                      onChange={(e) => setNewSession({ ...newSession, requiresPayment: e.target.checked })}
                      className="w-5 h-5 rounded border-gray-600 text-green-500 focus:ring-green-500"
                    />
                    <span>Require payment to join</span>
                  </label>
                </div>
                
                {newSession.requiresPayment && (
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Session Price ($)</label>
                    <input
                      type="number"
                      value={newSession.price}
                      onChange={(e) => setNewSession({ ...newSession, price: parseInt(e.target.value) || 0 })}
                      min="1"
                      className="w-full px-4 py-3 bg-[#0a0a0a] border border-gray-800 rounded-xl focus:border-green-500 focus:outline-none"
                    />
                  </div>
                )}
                
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Notes (optional)</label>
                  <textarea
                    value={newSession.notes}
                    onChange={(e) => setNewSession({ ...newSession, notes: e.target.value })}
                    rows={3}
                    className="w-full px-4 py-3 bg-[#0a0a0a] border border-gray-800 rounded-xl focus:border-green-500 focus:outline-none resize-none"
                    placeholder="Internal notes about this session..."
                  />
                </div>
              </div>
              
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 py-3 bg-gray-800 hover:bg-gray-700 rounded-xl font-semibold"
                >
                  Cancel
                </button>
                <button
                  onClick={createSession}
                  className="flex-1 py-3 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-400 hover:to-emerald-400 text-black font-bold rounded-xl"
                >
                  Create Session
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminSessionManager;
